import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Switch from 'components/Kizen/Switch';
import TextInput from 'components/Inputs/TextInput';
import {
  STEPS,
  // LIST_OF_FIELDS as allFieldTypes,
  LIST_OF_FORM_ACTIVITY_FIELDS as allFieldTypes,
} from 'components/GenericWizard/wizards/field/config';
import FieldType from 'components/GenericWizard/wizards/field/sections/type';
import { StyledTextAreaInput } from 'components/GenericWizard/styles';
import Cols from 'components/Layout/Cols';
import Section from '../section';
import { useFlashTransition } from 'hooks/useFlashState';
import Select from 'components/Inputs/Select';
import { DESCRIPTION_VISIBILITY } from 'utility/constants';

const exceptions = [
  'category',
  'name',
  'displayName',
  'type',
  'isRequired',
  'description',
  'descriptionVisibility',
];

const FieldSettingsBasic = ({
  updateField,
  resetField,
  updateValidation,
  formData,
  data,
  shownOn = null,
  errors,
  isActivityField,
}) => {
  const { t } = useTranslation();

  const isEdit = !!data.field?.id;
  const isValid = Boolean(
    formData.displayName.trim() && (!isEdit || formData.name.trim())
  );
  // activities has ActivityRelationshipOptions
  const fieldTypes = allFieldTypes.filter(
    (f) => !f.shownOn || (shownOn && f.shownOn.includes(shownOn))
  );

  useEffect(() => {
    updateValidation(STEPS.FIELD_SETTINGS, isValid);
  }, [isValid, updateValidation]);

  const [error, showError, flashError, cancelFlash] = useFlashTransition();
  useEffect(() => {
    if (errors?.name || errors?.displayName || errors?.description) {
      flashError(errors);
    } else {
      cancelFlash();
    }
  }, [errors, flashError, cancelFlash]);

  const options = DESCRIPTION_VISIBILITY.map(({ value, label }) => ({
    value: value,
    label: label(t),
  }));

  return (
    <>
      <Section index={0} header={t('Field Settings')} isActive>
        <Cols columns={2} gutter="20px">
          <TextInput
            label={t('Field Name')}
            value={formData.displayName}
            placeholder={t('Enter Field Name')}
            onChange={(value) => updateField('displayName', value)}
            error={errors?.displayName}
            validate={
              errors?.displayName && {
                message: error?.displayName,
                showMessage: error?.displayName && showError,
              }
            }
            margin={true}
          />
          <Switch
            label={t('Field is Required')}
            textPlacement="regular-top"
            checked={formData.isRequired}
            value="true"
            onChange={(e) => updateField('isRequired', e.target.checked)}
            disabled={data && data.field && data.field.isHidden}
            margin={true}
          />
        </Cols>
        {isEdit ? (
          <Cols columns={2} gutter="20px">
            <TextInput
              label={t('Field API Name')}
              value={formData.name}
              placeholder={t('Enter Field API Name')}
              onChange={(value) =>
                updateField('name', (value || '').toLowerCase())
              }
              margin={true}
              error={errors?.name}
              validate={
                errors?.name && {
                  message: error?.name,
                  showMessage: error?.name && showError,
                }
              }
            />
            {!isActivityField ? (
              <TextInput label={t('Field ID')} value={formData.id} disabled />
            ) : null}
          </Cols>
        ) : null}
        {isActivityField ? (
          <Cols columns={2} gutter="20px">
            <StyledTextAreaInput
              label={t('Description')}
              enableResize={false}
              value={formData.description}
              placeholder={t('Enter Description')}
              onChange={(value) => updateField('description', value)}
              validate={{
                message: error?.description,
                showMessage: error?.description && showError,
              }}
              error={errors?.description}
              margin={true}
              fullHeight={true}
              rows={5}
            />
            <Select
              fullWidth
              label={t('Description Visibility')}
              options={options}
              value={formData.descriptionVisibility}
              onChange={({ value }) =>
                updateField('descriptionVisibility', value)
              }
              margin={true}
            />
          </Cols>
        ) : null}
      </Section>
      <FieldType
        formData={formData}
        updateField={updateField}
        resetField={resetField}
        updateValidation={updateValidation}
        index={1}
        isActive
        data={data}
        fieldTypes={fieldTypes}
        exceptions={exceptions}
      />
    </>
  );
};

export default FieldSettingsBasic;
